import type { NonNullRef } from '@lemonade-hq/cdk';
import type { SchemaData } from '@lemonade-hq/chat-engine';
import {
  useAnswersResync,
  useSingleStepWizardAnswers,
  validateConditionalItem,
} from '@lemonade-hq/chat-wizard-client-base';
import type { OptionallyConditionalItem } from '@lemonade-hq/chat-wizard-common-definitions';
import type { GenericRecordData, RecordShape } from '@lemonade-hq/maschema-schema';
import { paths } from '@lemonade-hq/maschema-schema';
import type { FlatValidationsTree } from '@lemonade-hq/maschema-validations';
import { devWarn } from '@lemonade-hq/ts-helpers';
import type { Pixels } from '@lemonade-hq/ts-helpers';
import { useUniclientDialogContext, useUniclientTrackingContext } from '@lemonade-hq/uniclient-app';
import type { UniclientAction, UniclientDialogStep } from '@lemonade-hq/uniclient-chat';
import { changeValues, compose } from '@lemonade-hq/uniclient-chat';
import {
  Button,
  Dialog,
  Flex,
  Layout,
  LinkButton,
  ScrollGradient,
  spacing,
  Text,
  useMobileKeyboardHeight,
  useViewportSize,
} from '@lemonade-hq/uniclient-theme';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { clsx } from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { WizardImage } from '../adapters/components/Image/WizardImage';
import { DynamicComponent } from '../adapters/DynamicComponent';
import { useUniclientExtension } from '../hooks/uniclientExtension/useUniclientExtension';
import * as styles from './WizardDialog.css';
import { WizardFormattedText } from './WizardFormattedText';

interface WizardDialogStepProps {
  readonly step: UniclientDialogStep;
  readonly onActionRef: NonNullRef<(action?: UniclientAction) => Promise<unknown>>;
  readonly answersSchema: RecordShape;
  readonly isActionsLoading: (action?: UniclientAction) => boolean;
  readonly isLoading: boolean;
  readonly answers: GenericRecordData;
  readonly updateAnswers: (newAnswers: SchemaData) => void;
  readonly failingValidations?: FlatValidationsTree;
  readonly performAction: (action?: UniclientAction) => Promise<void>;
  readonly disableButtonOnInitialLoad: boolean;
  readonly hasFailingStrictSubmissionValidations: boolean;
  readonly actionsOverride?: {
    readonly primary?: () => void;
    readonly secondary?: () => void;
  };
  readonly disclaimer?: string;
  // Only applied for mobile viewport
  readonly fullscreen?: boolean;
  readonly preActionCallback?: () => Promise<boolean>;
  readonly isPrimaryActionDisabled: boolean;
  readonly enableScroll?: boolean;
}
const HEIGHT_FOR_SCROLL: Pixels = 360;
const CLOSE_BUTTON_WIDTH = spacing.s20;

const WizardDialogStep: React.FC<WizardDialogStepProps> = ({
  step,
  onActionRef,
  answersSchema,
  updateAnswers,
  failingValidations,
  performAction,
  answers,
  actionsOverride,
  isActionsLoading,
  fullscreen = false,
  disableButtonOnInitialLoad,
  hasFailingStrictSubmissionValidations,
  isLoading,
  disclaimer,
  preActionCallback,
  isPrimaryActionDisabled,
  enableScroll = true,
}) => {
  const { isBelowDesktopViewport } = useViewportSize();
  const [isNativeKeyboardOpen, setIsNativeKeyboardOpen] = useState(false);
  const {
    props: { dynamicHeight },
  } = useUniclientDialogContext();

  useMobileKeyboardHeight({ onChange: setIsNativeKeyboardOpen });

  const isPrimaryActionLoading = useMemo(() => isActionsLoading(step.action), [isActionsLoading, step.action]);

  const isSecondaryActionLoading = useMemo(
    () => isActionsLoading(step.secondaryAction),
    [isActionsLoading, step.secondaryAction],
  );

  const isSecondaryActionDisabled = useMemo(() => {
    if (isSecondaryActionLoading) return true;

    const hasFailingValidations = failingValidations != null && Object.keys(failingValidations).length > 0;

    return hasFailingValidations || disableButtonOnInitialLoad;
  }, [disableButtonOnInitialLoad, failingValidations, isSecondaryActionLoading]);

  const onPrimaryActionClick = useCallback(async () => {
    if (isPrimaryActionDisabled) return;
    if (preActionCallback != null) {
      const shouldContinue = await preActionCallback();
      if (!shouldContinue) return;
    }

    if (actionsOverride?.primary != null) {
      actionsOverride.primary();
    } else {
      void performAction(step.action);
    }
  }, [actionsOverride, performAction, step.action, preActionCallback, isPrimaryActionDisabled]);

  const onSecondaryActionClick = useCallback(
    () => (actionsOverride?.secondary != null ? actionsOverride.secondary() : void performAction(step.secondaryAction)),
    [actionsOverride, performAction, step.secondaryAction],
  );

  const onBottomActionClick = useCallback(
    () => void performAction(step.bottomAction),
    [performAction, step.bottomAction],
  );

  const isFullHeight = (isBelowDesktopViewport && (fullscreen || isNativeKeyboardOpen)) || dynamicHeight;

  const hasHeaderContent = useMemo(
    () => step.title.length > 0 || step.subtitle != null || step.thumbnail != null,
    [step.title, step.subtitle, step.thumbnail],
  );

  return (
    <Flex
      data-animated
      flexDirection="column"
      gap={spacing.s32}
      height="100%"
      justifyContent="space-between"
      key={step.title}
      paddingInline={{ sm: spacing.s08, md: 0 }}
    >
      <Flex alignItems="center" flexDirection="column" gap={spacing.s24} overflowY="hidden">
        {hasHeaderContent && (
          <Flex
            alignItems="center"
            flexDirection="column"
            gap={spacing.s08}
            maxWidth={{ md: `calc(100% - ${CLOSE_BUTTON_WIDTH})` }}
          >
            {step.thumbnail != null &&
              (typeof step.thumbnail === 'string' ? (
                <Layout alt={step.title} as="img" height={spacing.s56} src={step.thumbnail} />
              ) : (
                <WizardImage {...step.thumbnail} />
              ))}
            <Flex flexDirection="column" gap={spacing.s08}>
              <Dialog.Title>
                <Text as="span" display="block" type="h4">
                  {step.title}
                </Text>
              </Dialog.Title>
              {step.subtitle != null ? (
                <Dialog.Description>
                  <WizardFormattedText as="span" color="$textPrimary" text={step.subtitle} type="bodyMedium" />
                </Dialog.Description>
              ) : (
                <VisuallyHidden.Root>
                  <Dialog.Description>{step.title}</Dialog.Description>
                </VisuallyHidden.Root>
              )}
            </Flex>
          </Flex>
        )}

        {step.component != null && (
          <ScrollGradient
            className={clsx({ [styles.dialogComponent]: enableScroll, [styles.noScroll]: !enableScroll })}
            maxHeight={isFullHeight ? '100%' : HEIGHT_FOR_SCROLL}
          >
            <Flex
              data-animated-dialog
              data-testid="wizard-dialog-component"
              flexDirection="column"
              gap={spacing.s16}
              textAlign={step.alignComponentLeft ? 'left' : 'center'}
            >
              <Layout
                as={DynamicComponent}
                {...step}
                alignItems="center"
                answers={answers}
                answersSchema={answersSchema}
                disabled={isLoading}
                failingValidations={failingValidations}
                onAction={onActionRef.current}
                onChange={updateAnswers}
                showError={hasFailingStrictSubmissionValidations}
              />
            </Flex>
          </ScrollGradient>
        )}
      </Flex>

      {!isNativeKeyboardOpen && (
        <Flex flexDirection="column" gap={spacing.s16}>
          <Flex
            alignItems="flex-end"
            aria-label="dialog actions"
            flex={1}
            flexDirection={step.actionsLayout === 'column' ? 'column-reverse' : 'row'}
            gap={spacing.s16}
          >
            {step.secondaryAction != null && (
              <Layout
                as={Button}
                className={styles.dialogButton}
                data-animated
                disabled={isPrimaryActionLoading}
                loading={isLoading && isSecondaryActionLoading}
                onClick={onSecondaryActionClick}
                width="100%"
              >
                {step.secondaryAction.content}
              </Layout>
            )}
            <Layout
              as={Button}
              className={styles.dialogButton}
              data-animated
              disabled={isLoading || isSecondaryActionDisabled || isPrimaryActionDisabled}
              loading={isLoading || isPrimaryActionLoading}
              onClick={() => void onPrimaryActionClick()}
              variant={step.action.primary !== false ? 'primary' : 'secondary'}
              width="100%"
            >
              {step.action.content}
            </Layout>
          </Flex>
          {disclaimer != null && (
            <Text color="$textTertiary" type="bodySmall">
              {disclaimer}
            </Text>
          )}
          {step.bottomAction != null && (
            <LinkButton className={styles.linkButton} onClick={onBottomActionClick}>
              {step.bottomAction.content}
            </LinkButton>
          )}
        </Flex>
      )}
    </Flex>
  );
};

export interface WizardDialogProps {
  readonly steps: UniclientDialogStep[];
  readonly answersSchema: RecordShape;
  readonly persistedAnswers: GenericRecordData;
  readonly onActionRef: NonNullRef<(action?: UniclientAction) => Promise<unknown>>;
  readonly onSubmit?: (answers: GenericRecordData) => void;
  readonly actionsOverride?: {
    readonly primary?: () => void;
    readonly secondary?: () => void;
  };
  readonly fullscreen?: boolean;
  readonly disclaimer?: string;
}

interface ReplaceSaveDialogValuesActionProps {
  readonly action: UniclientAction;
  readonly answers: GenericRecordData;
  readonly onSaveDialogValuesAction?: (answers: GenericRecordData) => void;
}

const replaceSaveDialogValuesAction = ({
  action,
  answers,
  onSaveDialogValuesAction,
}: ReplaceSaveDialogValuesActionProps): UniclientAction => {
  if (action.type === 'compose') {
    return {
      ...action,
      actions: action.actions.map(a => replaceSaveDialogValuesAction({ action: a, answers, onSaveDialogValuesAction })),
    };
  }

  if (action.type === 'saveDialogValues') {
    onSaveDialogValuesAction?.(answers);

    return changeValues({
      data: action.data.map(item => ({
        attribute: item.targetAttribute,
        value: item.dialogAttribute == null ? answers : (answers[item.dialogAttribute] as GenericRecordData),
        strategy: item.strategy,
      })),
    });
  }

  if (action.type === 'asyncRequest') {
    return {
      ...action,
      initialValues: answers,
    };
  }

  return action;
};

export const WizardDialog: React.FC = () => {
  const {
    props: {
      name,
      steps,
      answersSchema,
      persistedAnswers,
      onActionRef,
      onSubmit,
      actionsOverride,
      resyncingActions,
      fullscreen,
      disclaimer,
      resyncingDebounce,
      preActionCallback,
      loading = false,
      enableScroll = true,
    },
    isLoading,
    isPrimaryActionDisabled,
  } = useUniclientDialogContext();
  const [currentRunningAction, setCurrentRunningAction] = useState<UniclientAction | undefined>();
  const attributes = useMemo(() => paths(answersSchema), [answersSchema]);
  const { trackInvalidAnswer } = useUniclientTrackingContext();
  const { answers, updateAnswers, failingValidations, hasFailingStrictSubmissionValidations } =
    useSingleStepWizardAnswers({
      persistedAnswers,
      answersSchema,
      attributes,
      onInvalidAnswer: trackInvalidAnswer,
    });

  const performAction = useCallback(
    async (action?: UniclientAction) => {
      if (action == null) return;

      setCurrentRunningAction(action);

      await onActionRef.current(replaceSaveDialogValuesAction({ action, answers, onSaveDialogValuesAction: onSubmit }));

      setCurrentRunningAction(undefined);
    },
    [onSubmit, onActionRef, answers],
  );

  useUniclientExtension(
    {
      name,
      failingValidations,
      currentStepAnswers: answers,
      answersSchema,
    },
    {
      prefix: 'dialog',
    },
  );

  useAnswersResync<UniclientAction>({
    currentStepData: { resyncingAttributes: Object.keys(resyncingActions ?? {}), resyncingDebounce },
    answers,
    performAction: async action => {
      if ('attributes' in action) {
        const actions =
          action.attributes
            ?.map(attribute => {
              const attributeAction = resyncingActions?.[attribute] as OptionallyConditionalItem<UniclientAction>;

              const shouldResync = validateConditionalItem(attributeAction, answers);

              if (!shouldResync) {
                devWarn(`Condition for resyncing action not met (attribute: ${attribute})`);
                return null;
              }

              return attributeAction;
            })
            .filter((a): a is OptionallyConditionalItem<UniclientAction> => a != null) ?? [];

        if (actions.length === 0) return;

        const composeAction = compose({
          actions,
        });

        await onActionRef.current(replaceSaveDialogValuesAction({ action: composeAction, answers }));
      }
    },
  });

  const isActionsLoading = useCallback(
    (action?: UniclientAction) => {
      if (action == null || currentRunningAction == null) return false;
      if (Array.isArray(action)) return action.includes(currentRunningAction);

      return action === currentRunningAction;
    },
    [currentRunningAction],
  );

  // When the dialog load failingValidation is an empty object at first which enables the button
  const disableButtonOnInitialLoad =
    failingValidations != null &&
    Object.keys(failingValidations).length === 0 &&
    Object.keys(answersSchema.schema).length > 0 &&
    Object.keys(answers).length <= 1;

  return (
    <Layout className={clsx({ [styles.dialogWrapper]: enableScroll })} height="100%" textAlign="center">
      {steps.map(step => (
        <WizardDialogStep
          actionsOverride={actionsOverride}
          answers={answers}
          answersSchema={answersSchema}
          disableButtonOnInitialLoad={disableButtonOnInitialLoad}
          disclaimer={disclaimer}
          enableScroll={enableScroll}
          failingValidations={failingValidations}
          fullscreen={fullscreen}
          hasFailingStrictSubmissionValidations={hasFailingStrictSubmissionValidations}
          isActionsLoading={isActionsLoading}
          isLoading={isLoading || loading}
          isPrimaryActionDisabled={isPrimaryActionDisabled}
          key={step.title}
          onActionRef={onActionRef}
          performAction={performAction}
          preActionCallback={preActionCallback}
          step={step}
          updateAnswers={updateAnswers}
        />
      ))}
    </Layout>
  );
};
